import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, Mail, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

import { PRODUCT_NAME } from "@/lib/product";

export const PRIVACY_NOTICE_LAST_UPDATED = "27 August 2026";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: `Privacy Policy · ${PRODUCT_NAME} Wallet` },
      {
        name: "description",
        content:
          "How GENRL collects, uses, stores, shares, and protects personal information, including Google Sign-In data.",
      },
    ],
  }),
  component: PrivacyPolicyPage,
});

const SECTION_LINKS = [
  ["summary", "Summary"],
  ["information", "Information we handle"],
  ["google", "Google Sign-In"],
  ["uses", "How we use information"],
  ["sharing", "Sharing"],
  ["retention", "Retention"],
  ["rights", "Your rights"],
] as const;

export function PrivacyPolicyPage() {
  return (
    <main className="wallet-ops-shell min-h-screen px-4 py-8 text-[var(--wallet-ops-fg)] sm:py-12">
      <div className="wallet-ops-container mx-auto max-w-5xl space-y-6">
        <header className="wallet-ops-panel overflow-hidden p-6 sm:p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-3xl">
              <div className="wallet-ops-metric-icon">
                <ShieldCheck aria-hidden="true" className="h-4 w-4" />
              </div>
              <p className="wallet-ops-label mt-5">Legal and privacy</p>
              <h1 className="mt-2 text-[32px] font-semibold tracking-normal sm:text-[40px]">
                Privacy Policy
              </h1>
              <p className="mt-4 max-w-2xl text-[14px] leading-7 text-[var(--wallet-ops-subtle)]">
                This notice explains how General Distribution Co., trading as GENRL ("GENRL", "we",
                "us"), handles personal information when you use the GENRL Wallet, our websites,
                APIs, managed compute services, and related support channels (together, the
                "Services").
              </p>
            </div>
            <div className="rounded-2xl border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-4 py-3 text-[12px] leading-5 text-[var(--wallet-ops-subtle)]">
              <p className="font-semibold text-[var(--wallet-ops-fg)]">Last updated</p>
              <p>{PRIVACY_NOTICE_LAST_UPDATED}</p>
            </div>
          </div>

          <nav aria-label="Privacy policy sections" className="mt-7 flex flex-wrap gap-2">
            {SECTION_LINKS.map(([id, label]) => (
              <a key={id} href={`#${id}`} className="wallet-ops-button min-h-9 px-3 text-[11px]">
                {label}
              </a>
            ))}
          </nav>
        </header>

        <PolicySection id="summary" title="1. Summary and scope">
          <p>
            We process personal information only for defined purposes connected with operating,
            securing, supporting, and improving the Services; administering accounts and
            organisations; providing compute and storage functionality; handling payments; complying
            with law; and protecting users, GENRL, and third parties.
          </p>
          <p>
            This notice applies where GENRL acts as a controller. If your employer, customer, or
            another organisation provides your account or submits data to the Services, that
            organisation may be the controller and GENRL may act as its processor. In that case,
            direct requests about organisation-controlled data to that organisation first. A signed
            order form, data processing agreement, or service agreement may add to this notice and
            will control if it expressly states different terms.
          </p>
          <p>
            Please do not submit special-category information, government identifiers, health
            information, or other highly sensitive personal information unless it is necessary for
            an agreed Service and you are authorised to do so.
          </p>
        </PolicySection>

        <PolicySection id="controller" title="2. Who is responsible and how to contact us">
          <p>
            General Distribution Co., trading as GENRL, is responsible for the controller activities
            described in this notice. A contract or order may identify a specific GENRL contracting
            entity for your account.
          </p>
          <div className="mt-4 rounded-2xl border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
            <p className="font-semibold text-[var(--wallet-ops-fg)]">Privacy contact</p>
            <a
              className="mt-2 inline-flex items-center gap-2 text-[var(--wallet-ops-link)] underline underline-offset-4"
              href="mailto:team@genrl.co?subject=Privacy%20request"
            >
              <Mail aria-hidden="true" className="h-4 w-4" />
              team@genrl.co
            </a>
            <p className="mt-2 text-[12px] leading-6">
              You may also use the support channel in the Wallet. Do not send passwords, private
              keys, full payment-card details, or unredacted provider credentials in a privacy
              request.
            </p>
          </div>
        </PolicySection>

        <PolicySection id="information" title="3. Information we collect or otherwise process">
          <PolicyList
            items={[
              <>
                <strong>Account and identity information:</strong> name, username, email address,
                email-verification status, profile image, identity-provider and account identifiers,
                organisation membership, role, and account settings.
              </>,
              <>
                <strong>Access-request and business information:</strong> company, job title,
                intended compute use, referral or campaign source, the page from which a request was
                submitted, and communications about access.
              </>,
              <>
                <strong>Organisation and service information:</strong> workspaces, permissions, team
                members, provider connections, compute plans, jobs, instances, storage resources,
                workloads, artifacts, configuration, resource names, regions, usage, metering, and
                operational status.
              </>,
              <>
                <strong>Credentials and connection material:</strong> tokens, public keys,
                service-account details, provider credentials, endpoint information, and other
                secrets you choose to supply so we can connect or operate a Service. Treat these
                fields as sensitive and provide only the minimum required.
              </>,
              <>
                <strong>Payment and commercial information:</strong> billing contact, plan, order,
                subscription, credit balance, transaction, invoice, tax, payment status,
                payment-provider identifiers, and fraud or reconciliation data. Card details entered
                in hosted checkout are processed by the payment provider; GENRL may receive limited
                card and transaction metadata but does not need your full card number or card
                security code.
              </>,
              <>
                <strong>Support and communications:</strong> messages, attachments, feedback,
                incident reports, and records of actions taken to resolve a request.
              </>,
              <>
                <strong>Device, network, and security information:</strong> IP address, user agent,
                browser and device characteristics, timestamps, requested URLs, session and
                authentication events, diagnostic records, error data, audit events, and security
                signals.
              </>,
              <>
                <strong>Cookies and local storage:</strong> strictly necessary session,
                authentication, security, routing, preference, and user-scoped cache data. You can
                clear browser storage, but doing so may sign you out or reset preferences.
              </>,
              <>
                <strong>Derived information:</strong> account status, usage summaries, capacity or
                price estimates, risk indicators, entitlement decisions, and other inferences
                produced from the information above.
              </>,
            ]}
          />
          <p>
            We receive information directly from you and your organisation, from identity and
            payment providers, from compute and cloud providers you connect, from devices and
            browsers, and from partners or public sources where lawful and relevant. If you provide
            information about another person, you must have a lawful basis and authority to do so.
          </p>
        </PolicySection>

        <PolicySection id="google" title="4. Google Sign-In and Google user data">
          <div className="rounded-2xl border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-5">
            <p className="font-semibold text-[var(--wallet-ops-fg)]">Plain-language disclosure</p>
            <p className="mt-2">
              If you choose "Continue with Google", Google authenticates you and Auth0 passes GENRL
              the profile information needed to create, locate, secure, and display your Wallet
              account. Depending on what Google makes available and you authorise, this can include
              your Google account identifier, name, email address, verified-email status, profile
              image, public profile information, country, language, locale, and time-zone
              information.
            </p>
          </div>
          <PolicyList
            items={[
              <>
                We use Google Sign-In data only for authentication, account creation and linking,
                profile display, access control, security, fraud prevention, support, and
                compliance.
              </>,
              <>
                Google Sign-In does <strong>not</strong> give GENRL access to your Gmail, Google
                Drive files, Calendar, Contacts, Photos, Sheets, Slides, YouTube, browsing history,
                or other Google content. The Wallet login connection does not request those scopes.
              </>,
              <>
                We do not intentionally request offline Google access or a Google refresh token for
                ordinary Wallet sign-in. Authentication providers may maintain their own session
                cookies and security records under their policies.
              </>,
              <>
                We do not sell Google user data, use it for targeted advertising, transfer it to
                data brokers, use it for surveillance, or use it to train a general-purpose AI
                model.
              </>,
              <>
                We share Google Sign-In data only with Auth0 and the infrastructure and service
                providers needed to authenticate and operate your account, or where law, security,
                or your explicit direction requires it.
              </>,
              <>
                You can revoke GENRL's Google access in your Google Account. Revocation prevents
                future Google authentication but does not automatically delete the GENRL account or
                records we must retain. Contact us to request account deletion.
              </>,
            ]}
          />
          <div className="flex flex-wrap gap-3 text-[12px]">
            <ExternalPolicyLink href="https://myaccount.google.com/permissions">
              Manage Google access
            </ExternalPolicyLink>
            <ExternalPolicyLink href="https://policies.google.com/privacy">
              Google Privacy Policy
            </ExternalPolicyLink>
            <ExternalPolicyLink href="https://developers.google.com/terms/api-services-user-data-policy">
              Google API Services User Data Policy
            </ExternalPolicyLink>
          </div>
        </PolicySection>

        <PolicySection id="uses" title="5. Purposes and lawful bases">
          <p>
            The lawful basis depends on the purpose, the relationship, and local law. We determine
            and document the applicable basis before processing. The principal purposes and bases
            are:
          </p>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-[var(--wallet-ops-border)]">
            <table className="w-full min-w-[680px] border-collapse text-left text-[12px] leading-6">
              <thead className="bg-[var(--wallet-ops-surface)] text-[var(--wallet-ops-fg)]">
                <tr>
                  <th className="p-3 font-semibold">Purpose</th>
                  <th className="p-3 font-semibold">Typical lawful basis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--wallet-ops-border)]">
                <LawfulBasisRow
                  purpose="Provide accounts, authentication, compute, storage, billing, and support"
                  basis="Taking requested steps and performing a contract; legitimate interests in operating the Services"
                />
                <LawfulBasisRow
                  purpose="Secure systems; prevent abuse, fraud, and unauthorised access; investigate incidents"
                  basis="Legitimate interests; legal obligation where applicable"
                />
                <LawfulBasisRow
                  purpose="Maintain financial, tax, corporate, sanctions, and compliance records"
                  basis="Legal obligation; legitimate interests in establishing and defending legal claims"
                />
                <LawfulBasisRow
                  purpose="Improve reliability, performance, accessibility, and product design"
                  basis="Legitimate interests, using proportionate and minimised information"
                />
                <LawfulBasisRow
                  purpose="Send requested service messages and respond to enquiries"
                  basis="Contract steps; legitimate interests; legal obligation"
                />
                <LawfulBasisRow
                  purpose="Optional marketing or non-essential technologies"
                  basis="Consent where required; otherwise a documented legitimate interest with an opt-out"
                />
              </tbody>
            </table>
          </div>
          <p>
            Where we rely on legitimate interests, we consider necessity, proportionality, and the
            impact on individuals. Where we rely on consent, you may withdraw it at any time without
            affecting earlier lawful processing.
          </p>
        </PolicySection>

        <PolicySection id="sharing" title="6. How and why information is shared">
          <p>We may disclose the minimum information reasonably necessary to:</p>
          <PolicyList
            items={[
              <>
                <strong>Identity providers</strong>, including Auth0 and Google, to authenticate
                users, protect accounts, and maintain sessions.
              </>,
              <>
                <strong>Infrastructure and technology providers</strong> that host, transmit,
                monitor, secure, back up, or support the Services.
              </>,
              <>
                <strong>Compute, cloud, storage, and network providers</strong> selected by you or
                required to fulfil a workload, provision a resource, or provide support. Workload
                and account information may be sent to the relevant provider as necessary to perform
                the requested operation.
              </>,
              <>
                <strong>Payment, banking, accounting, and fraud-prevention providers</strong>,
                including Stripe where offered, to process and reconcile payments and comply with
                financial rules.
              </>,
              <>
                <strong>Your organisation</strong>, its administrators, and authorised workspace
                members according to roles, permissions, contracts, and account settings.
              </>,
              <>
                <strong>
                  Professional advisers, auditors, insurers, regulators, courts, and authorities
                </strong>{" "}
                when reasonably necessary for advice, compliance, claims, security, or legal
                process.
              </>,
              <>
                <strong>A successor or transaction participant</strong> in a financing,
                reorganisation, merger, sale, or transfer, subject to appropriate confidentiality
                and lawful-use restrictions.
              </>,
            ]}
          />
          <p>
            We do not sell personal information. We do not disclose personal information for
            cross-context behavioural advertising. Service providers may process information only
            for the services they provide to us, subject to their contracts and applicable law;
            independent providers may also act as separate controllers under their own notices.
          </p>
        </PolicySection>

        <PolicySection id="transfers" title="7. International transfers">
          <p>
            GENRL and its providers may process information in the United Kingdom, European Economic
            Area, United States, and other countries where GENRL, a connected compute provider, or a
            service provider operates. Laws in those countries may differ from those where you live.
          </p>
          <p>
            Where a restricted transfer requires protection, we use an applicable lawful mechanism,
            such as adequacy regulations or decisions, contractual safeguards (including an
            applicable UK transfer agreement or addendum), or another permitted mechanism, and
            supplementary measures where appropriate. Contact us for information about the mechanism
            relevant to your data, subject to confidentiality and security limits.
          </p>
        </PolicySection>

        <PolicySection id="retention" title="8. Retention and deletion">
          <p>
            We retain information only for as long as reasonably necessary for the purpose
            collected, including providing the Services, maintaining security and audit trails,
            resolving disputes, enforcing agreements, and meeting legal, accounting, tax, sanctions,
            and regulatory obligations. Relevant criteria include account status, contract duration,
            resource lifecycle, sensitivity, statutory limitation periods, legal holds, incident
            needs, and backup cycles.
          </p>
          <PolicyList
            items={[
              <>
                Account and organisation records generally remain while the account or business
                relationship is active and for a proportionate closure period.
              </>,
              <>
                Access requests and sales enquiries are reviewed periodically and deleted or
                minimised when they are no longer reasonably needed.
              </>,
              <>
                Authentication, security, diagnostic, and audit records are retained on risk-based
                schedules and may be preserved longer for an active investigation.
              </>,
              <>
                Billing, transaction, tax, and contractual records may be retained for the period
                required by financial law and limitation rules.
              </>,
              <>
                Workloads, artifacts, storage, and provider resources follow the selected product,
                workspace, provider, deletion, and backup lifecycle. Deleting a GENRL record may not
                delete a resource held in an independently controlled provider account.
              </>,
              <>
                Backups and immutable logs may persist until their ordinary expiry and are isolated
                from routine use where deletion cannot be immediate.
              </>,
            ]}
          />
          <p>
            When retention is no longer justified, we delete, anonymise, aggregate, or isolate the
            information. Valid deletion requests are handled without undue delay, subject to
            identity verification, third-party dependencies, legal exceptions, and the rights of
            others.
          </p>
        </PolicySection>

        <PolicySection id="security" title="9. Security and incident handling">
          <p>
            We use technical and organisational measures designed to protect information against
            accidental or unlawful destruction, loss, alteration, disclosure, or access. Depending
            on the system and risk, these measures may include access controls, authentication,
            least-privilege permissions, encryption in transit, protected secret storage, logging,
            monitoring, isolation, review, and tested recovery procedures.
          </p>
          <p>
            No online service can guarantee absolute security. You are responsible for protecting
            account credentials, limiting provider permissions, reviewing workspace membership, and
            promptly reporting suspected compromise. We assess incidents and notify affected people
            and authorities when required by law.
          </p>
        </PolicySection>

        <PolicySection id="rights" title="10. Your privacy rights">
          <p>
            Depending on your location and the circumstances, you may have rights to be informed;
            access personal information; correct inaccurate information; request deletion or
            restriction; object to certain processing, including direct marketing; receive portable
            information; withdraw consent; and complain to a supervisory authority. These rights are
            not absolute and exemptions may apply.
          </p>
          <p>
            Send a request to{" "}
            <a
              className="text-[var(--wallet-ops-link)] underline underline-offset-4"
              href="mailto:team@genrl.co?subject=Privacy%20request"
            >
              team@genrl.co
            </a>{" "}
            or use in-product support. Describe the right you wish to exercise and the relevant
            account or organisation. We may request proportionate information to verify identity and
            authority, but never send us a password, private key, or full payment-card details.
          </p>
          <p>
            UK users may complain to the Information Commissioner's Office. We encourage you to
            contact us first so we can investigate, but doing so does not limit your right to
            complain.
          </p>
          <ExternalPolicyLink href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/">
            Contact the UK ICO
          </ExternalPolicyLink>
        </PolicySection>

        <PolicySection id="automation" title="11. Automated processing and service decisions">
          <p>
            The Services use automated rules and technical signals for authentication, security,
            abuse prevention, access control, capacity matching, pricing, provisioning, metering,
            and operational decisions. Unless we tell you otherwise in a specific context, these
            processes are not intended to make a solely automated decision about an individual that
            produces legal or similarly significant effects. Contact us if you believe an automated
            decision has materially affected you and you want an explanation or human review where a
            legal right applies.
          </p>
        </PolicySection>

        <PolicySection id="children" title="12. Children">
          <p>
            The Services are business and technical services and are not directed to children under
            18. Do not create an account or submit a child's personal information unless an
            authorised enterprise arrangement and applicable law permit it. If we learn that a child
            provided information contrary to this notice, we will take proportionate steps to
            restrict or delete it.
          </p>
        </PolicySection>

        <PolicySection id="changes" title="13. Changes to this policy and third-party services">
          <p>
            Third-party sites, identity providers, payment providers, and independently controlled
            compute providers have their own terms and privacy notices. This policy does not control
            their independent processing.
          </p>
          <p>
            We may update this policy when the Services, providers, or law change. We will update
            the date above and, where a change is material, provide additional notice or request
            consent where required before using information for a new, incompatible purpose.
          </p>
        </PolicySection>

        <footer className="flex flex-col gap-3 pb-4 text-[12px] text-[var(--wallet-ops-subtle)] sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 General Distribution Co. All rights reserved.</p>
          <div className="flex flex-wrap gap-3">
            <a className="underline underline-offset-4" href="/terms-and-conditions">
              Terms and Conditions
            </a>
            <a className="underline underline-offset-4" href="/request-access">
              Back to GENRL Wallet
            </a>
          </div>
        </footer>
      </div>
    </main>
  );
}

function PolicySection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="wallet-ops-panel scroll-mt-6 p-6 sm:p-8">
      <h2 className="text-[20px] font-semibold tracking-normal text-[var(--wallet-ops-fg)] sm:text-[23px]">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[13px] leading-7 text-[var(--wallet-ops-subtle)] sm:text-[14px]">
        {children}
      </div>
    </section>
  );
}

function PolicyList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="ml-5 list-disc space-y-3 marker:text-[var(--wallet-ops-link)]">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function LawfulBasisRow({ purpose, basis }: { purpose: string; basis: string }) {
  return (
    <tr>
      <td className="p-3 align-top text-[var(--wallet-ops-fg)]">{purpose}</td>
      <td className="p-3 align-top text-[var(--wallet-ops-subtle)]">{basis}</td>
    </tr>
  );
}

function ExternalPolicyLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className="inline-flex items-center gap-1.5 text-[var(--wallet-ops-link)] underline underline-offset-4"
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      {children}
      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
    </a>
  );
}
